import { createRoot, createSignal } from 'solid-js';
import { geolocation } from '~/lib/geolocation';
import { heat } from '~/lib/heat';
import type { HackBand } from '~/lib/resolution';

/** Heat log buffer cap. At the 1.5s sample rate, 5000 rows ≈ 2 hours. */
export const HEAT_LOG_CAP = 5000;
/** Periodic sample interval — matches the heat decay tick so every decay step
 *  is observable in the export. */
export const HEAT_LOG_SAMPLE_MS = 1500;

export interface HeatLogRow {
  timestamp: number;
  heatValue: number;
  eventType: 'gain' | 'sample';
  gainAmount: number | null;
  gainBand: HackBand | null;
  originLat: number | null;
  originLng: number | null;
  targetTier: number | null;
  playerLat: number | null;
  playerLng: number | null;
  activeEventCount: number;
  isSimulated: boolean;
}

/** DIAG-only heat logger. Observes the heat store READ-ONLY (never mutates it
 *  or perturbs decay): each row snapshots the current global heat, the active
 *  event count, and the player position. Gain rows are appended by the
 *  HackModal after a hack resolves; sample rows are appended periodically so
 *  the decay slope between gains is visible. Mirrors the geolocation logger.
 *  Module-level singleton; persistence is not needed (diagnostics only). */
function createHeatLog() {
  const [rows, setRows] = createSignal<HeatLogRow[]>([]);

  const append = (row: HeatLogRow): void => {
    setRows((rs) => {
      const next = [...rs, row];
      if (next.length > HEAT_LOG_CAP) next.splice(0, next.length - HEAT_LOG_CAP);
      return next;
    });
  };

  const playerSnapshot = () => {
    const f = geolocation.latest();
    return f ? { lat: f.lat, lng: f.lng, simulated: f.simulated } : null;
  };

  const base = (eventType: 'gain' | 'sample'): HeatLogRow => {
    const p = playerSnapshot();
    return {
      timestamp: Date.now(),
      heatValue: heat.value(),
      eventType,
      gainAmount: null,
      gainBand: null,
      originLat: null,
      originLng: null,
      targetTier: null,
      playerLat: p?.lat ?? null,
      playerLng: p?.lng ?? null,
      activeEventCount: heat.events().length,
      isSimulated: p?.simulated ?? false,
    };
  };

  /** Periodic sample — only while there is heat to observe (skips idle 0s). */
  const sample = (): void => {
    if (heat.value() <= 0 && heat.events().length === 0) return;
    append(base('sample'));
  };

  /** Append a gain row for a resolved hack (called by the HackModal after the
   *  heat store has been updated). `amount` is 0 for a clean outcome. */
  const logGain = (
    band: HackBand,
    amount: number,
    origin: { lat: number; lng: number },
    tier: number,
  ): void => {
    append({
      ...base('gain'),
      gainAmount: amount,
      gainBand: band,
      originLat: origin.lat,
      originLng: origin.lng,
      targetTier: tier,
    });
  };

  if (typeof window !== 'undefined') {
    setInterval(sample, HEAT_LOG_SAMPLE_MS);
  }

  return {
    rows,
    count: () => rows().length,
    clear: (): void => {
      setRows([]);
    },
    sample,
    logGain,
  };
}

export const heatLog = createRoot(() => createHeatLog());
